/**
 * №29 (S1) — the work rail's MEMORY tab: what the member of staff working this has
 * learned, and is carrying into it.
 *
 * Source: the hire's own library subtree — feedback memories and the patterns
 * Sprint-21's `find_patterns` marked inside them (`useAgentLearnings`, R1 reuse of the
 * derivation the desk already uses). These are real nodes with real bodies; nothing
 * here is an LLM summary written for the panel.
 *
 * Two honest absences, and they are different, so they read differently:
 *
 *  - **no hire is carrying this conversation.** A matter, a Place or the workspace
 *    talks to Kuvio, the un-personified workspace chat (D-AF4) — which has no desk and
 *    therefore no learnings. The tab says that, rather than showing the workspace's
 *    memories under a heading that implies a person.
 *  - **the hire has recorded nothing yet.** A new hire genuinely has an empty memory,
 *    and an empty memory is a fact about the hire, not a gap in the panel.
 *
 * Seeded template memories are excluded by `useAgentLearnings` (Sprint 27.0 bug 3):
 * something shipped with the hire is not something it learned here.
 *
 * D-L6: a member of staff, a hire, a named role — never "the agent".
 */
import { useAgentLearnings, isPatternMemory } from "../../../hooks/use-agent-learnings";
import { RailEmpty, RailHead, RailLoading } from "./RailPrimitives";
import type { RailBinding } from "./rail-binding";
import type { LibraryNode } from "../../../../lib/types";

/** One clamped line of a memory's body — a row is a label, not a message (22.3.2 F2b). */
function snippet(node: LibraryNode): string {
  const body = (node.document ?? "").replace(/^---[\s\S]*?---/, "").replace(/[#*_`>]/g, " ").replace(/\s+/g, " ").trim();
  return body.length > 140 ? `${body.slice(0, 139)}…` : body;
}

function MemoryRow({ node }: { node: LibraryNode }) {
  const pattern = isPatternMemory(node);
  const confidence = node.confidence;
  return (
    <div className="railmem" data-testid="rail-memory-row" data-pattern={pattern ? "true" : undefined}>
      <div className="railmem__t">
        {pattern && <span className="railmem__tag">pattern</span>}
        {node.name}
        {confidence != null && <span className="railmem__c">{Math.round(confidence * 100)}%</span>}
      </div>
      {snippet(node) && <div className="railmem__s">{snippet(node)}</div>}
    </div>
  );
}

export function RailMemory({ binding }: { binding: RailBinding }) {
  const { hire } = binding;
  const learnings = useAgentLearnings(hire?.id);

  if (!hire) {
    return (
      <RailEmpty>
        No member of staff is carrying this conversation — <b>{binding.label}</b> talks to Kuvio, the
        workspace itself, which has no desk to learn at. Open a hire, or click one of their runs, to
        see what they carry.
      </RailEmpty>
    );
  }
  if (learnings.isLoading) return <RailLoading />;

  const memories = learnings.feedbackMemories;
  if (memories.length === 0) {
    return (
      <RailEmpty>
        <b>{hire.name}</b> has recorded nothing from this work yet. Learnings appear here when a
        retrospective writes one — an empty memory is a fact about the hire, not a missing panel.
      </RailEmpty>
    );
  }

  return (
    <div className="railpane" data-testid="rail-memory">
      <RailHead>
        {hire.name} carries {memories.length} learning{memories.length === 1 ? "" : "s"}
        {learnings.patterns.length > 0 ? ` · ${learnings.patterns.length} pattern` : ""}
        {learnings.patterns.length > 1 ? "s" : ""}
      </RailHead>
      {memories.map((m) => (
        <MemoryRow key={m.id} node={m} />
      ))}
    </div>
  );
}
